import React, { useState, useContext } from 'react'
import { useMutation } from '@apollo/client'
import {
  Grid,
  Header,
  Form,
  Button,
  Table,
  Message,
  Divider,
} from 'semantic-ui-react'
import DataContext from '../contexts/DataContext'
import EmploymentHistory from './Forms/Professional/EmploymentHistory'
import ProfessionalRegistration from './Forms/Professional/ProfessionalRegistration'
import { UPDATE_EXPERT } from '../graphql/expert'
import {
  PROFESSIONAL_REGISTRATION,
  PROFESSIONAL_REGISTRATION_TYPE_DIGITS,
} from '../constants/professionalRegistration'

const REGISTRATION_NUMBER_ERROR = /professionalRegistrations\.\d+\.number/

const WorkConfirmation = ({ project, onNext }) => {
  const { advisor, countries = [] } = useContext(DataContext)

  const [sameWork, setSameWork] = useState(null)
  const [employmentHistory, setEmploymentHistory] = useState(
    advisor.expert.employmentHistory
  )
  const [errorMsg, setErrorMsg] = useState(null)

  const countryCode = countries
    .find((c) => c.name === advisor.expert.country)
    ?.code?.toLowerCase()

  const registrationConfig = PROFESSIONAL_REGISTRATION[countryCode]
  const showRegistration =
    !!project.requiresProfessionalRegistration && !!registrationConfig

  const [professionalRegistrations, setProfessionalRegistrations] = useState(
    advisor.expert.professionalRegistrations || []
  )
  const registration = professionalRegistrations.find((item) =>
    registrationConfig?.types.includes(item.type)
  ) || { type: registrationConfig?.types[0], number: '' }
  const [noRegistration, setNoRegistration] = useState(false)

  const clearErrorAnd =
    (setter) =>
    (...args) => {
      setErrorMsg(null)
      setter(...args)
    }
  const handleSetProfessionalRegistrations = clearErrorAnd(
    setProfessionalRegistrations
  )
  const handleSetNoRegistration = clearErrorAnd(setNoRegistration)

  const [updateExpert, { loading }] = useMutation(UPDATE_EXPERT)

  const next = () => {
    setErrorMsg(null)
    if (
      employmentHistory.length === 0 ||
      employmentHistory.some((e) => !e.title || !e.organization)
    ) {
      return setErrorMsg(
        'Please fill out your employment information completely'
      )
    }
    if (showRegistration && !noRegistration && !registration.number.trim()) {
      return setErrorMsg(
        "Please enter your professional registration ID, or check the box if you don't have one."
      )
    }
    updateExpert({
      variables: {
        input: {
          employmentHistory: employmentHistory.map((e) => ({
            ...e,
            organization: e.organization.id,
          })),
          ...(showRegistration && {
            professionalRegistrations,
          }),
        },
      },
      context: { skipGlobalErrorHandling: true },
      onCompleted: onNext,
      onError: (error) => {
        if (REGISTRATION_NUMBER_ERROR.test(error.message)) {
          const type = registration.type
          const digits = PROFESSIONAL_REGISTRATION_TYPE_DIGITS[type]
          return setErrorMsg(
            digits
              ? `That ${type} doesn't look right. ${type}s are ${digits} digits — check the number and try again.`
              : `That ${type} doesn't look right. Check the number and try again.`
          )
        }
        setErrorMsg(error.message)
      },
    })
  }

  return (
    <Grid id="work-confirmation">
      <Grid.Row>
        <Grid.Column>
          <Header as="h5">
            {'Please confirm: Is your recent experience still up to date? '}
          </Header>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Organization</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {advisor.expert.employmentHistory.map((e) => (
                <Table.Row key={`${e.title}${e.organization.id}`}>
                  <Table.Cell>
                    {`${e.title} ${e.isCurrent ? ' (Current)' : ''}`}
                  </Table.Cell>
                  <Table.Cell>{e.organization.name}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Form>
            <Form.Radio
              label="Yes, my job details are correct."
              checked={sameWork === 'Yes'}
              onChange={() => setSameWork('Yes')}
            />
            <Form.Radio
              label="No, I need to update my information."
              checked={sameWork === 'No'}
              onChange={() => setSameWork('No')}
            />
          </Form>
        </Grid.Column>
      </Grid.Row>
      {sameWork === 'No' && (
        <Grid.Row>
          <Grid.Column>
            <Header as="h5">
              Please update your job details to ensure we have the most accurate
              information about you.
              <br /> <br />
              Be sure to list them in chronological order, starting with the
              most recent role.
            </Header>
            <Form>
              <EmploymentHistory
                employmentHistory={employmentHistory}
                setEmploymentHistory={setEmploymentHistory}
              />
            </Form>
          </Grid.Column>
        </Grid.Row>
      )}
      {showRegistration && (
        <>
          <Divider />
          <Grid.Row>
            <Grid.Column>
              <ProfessionalRegistration
                country={advisor.expert.country}
                professionalRegistrations={professionalRegistrations}
                setProfessionalRegistrations={
                  handleSetProfessionalRegistrations
                }
                noRegistration={noRegistration}
                setNoRegistration={handleSetNoRegistration}
              />
            </Grid.Column>
          </Grid.Row>
        </>
      )}
      <Grid.Row>
        <Grid.Column>
          {errorMsg && <Message negative>{errorMsg}</Message>}
          <Button
            primary
            loading={loading}
            disabled={sameWork === null || loading}
            floated="right"
            onClick={next}
          >
            Next
          </Button>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default WorkConfirmation
